import {useMutation, useLazyQuery} from '@apollo/client';
import {Form, Input, Select, Spin} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';

import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {GET_USER_APP_LANGUAGE} from 'graphql/queries/users';
import styles from './manualTxModal.module.scss';
import {
  CUSTOM_REASON,
  MANUAL_TX_REASONS,
  REASON_LANGUAGES,
  languageLabel,
  presetForText,
  reasonText,
} from './manualTxReasons';

/**
 * The reason an admin records against a manual transaction.
 *
 * Three controls over one submitted value. The dropdown and the language
 * picker exist only in this form — neither is sent. `reason` is still the
 * single free-text string the service has always stored, so nothing about the
 * mutation or the stored audit trail changes.
 *
 * Changing the language translates what is in the box, rather than merely
 * labelling it: the point is to record the reason in the language the user
 * reads. The four presets carry their own translations, so the common path
 * never touches the network; only a reason the admin typed themselves goes to
 * the translation service.
 */
const ManualTxReasonField = ({form, userId}) => {
  const [language, setLanguage] = useState('EN');
  const [preset, setPreset] = useState();
  const [translating, setTranslating] = useState(false);
  // Whether the admin has chosen a language for themselves. Once they have,
  // a late-arriving app language must not yank the box out from under them.
  const chosenRef = useRef(false);

  const [translateText] = useMutation(TRANSLATE_TEXT, {errorPolicy: 'all'});
  const [fetchLanguage, {data, loading: loadingLanguage}] = useLazyQuery(
    GET_USER_APP_LANGUAGE,
    {errorPolicy: 'all'},
  );

  useEffect(() => {
    if (userId) fetchLanguage({variables: {getUserByIdId: userId}});
  }, [userId, fetchLanguage]);

  const result = data?.getUserById;
  const appLanguage =
    result?.__typename === 'UserType' ? result?.user?.language : null;

  // Start in the language the user reads. Only before the admin has picked
  // one, and only while the box is still empty — translating away text that
  // is already written is the admin's call, not a side effect of a query
  // resolving.
  useEffect(() => {
    if (!appLanguage || chosenRef.current) return;
    if (String(form.getFieldValue('reason') ?? '').trim()) return;
    setLanguage(appLanguage);
  }, [appLanguage, form]);

  const setReason = value => form.setFieldsValue({reason: value});

  const handlePreset = next => {
    setPreset(next);
    setReason(next === CUSTOM_REASON ? '' : reasonText(next, language));
  };

  const handleLanguage = async next => {
    chosenRef.current = true;
    if (next === language) return;

    const current = String(form.getFieldValue('reason') ?? '').trim();
    // A preset carries its own translation, and an empty box has nothing to
    // translate. Either way this is just a relabel.
    const known = presetForText(current);
    if (!current || known) {
      setLanguage(next);
      if (known) setReason(reasonText(known, next));
      return;
    }

    setTranslating(true);
    try {
      const {data: translated} = await translateText({
        variables: {
          sourceLang: language,
          targetLang: next,
          sourceText: current,
        },
      });
      const response = translated?.translateText;
      if (!response?.targetText) {
        // Leave the language where it was: the box still holds the old
        // language's text, and moving the label would misdescribe it.
        return toast.error(
          response?.message || 'Could not translate the reason',
        );
      }
      setReason(response.targetText);
      setLanguage(next);
    } catch (error) {
      toast.error('Could not translate the reason');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <>
      <Form.Item label="Reason">
        <Select
          placeholder="Pick a reason"
          value={preset}
          options={MANUAL_TX_REASONS}
          onChange={handlePreset}
        />
      </Form.Item>

      <Form.Item
        label="Reason language"
        tooltip="Not sent with the transaction — it decides which language the reason below is written in."
        extra={
          loadingLanguage ? (
            'Checking the user’s app language…'
          ) : appLanguage ? (
            <span
              className={
                appLanguage !== language ? styles.languageMismatch : undefined
              }>
              This user reads the app in {languageLabel(appLanguage)}.
            </span>
          ) : (
            'This user has no app language set.'
          )
        }>
        <Select
          value={language}
          options={REASON_LANGUAGES}
          loading={translating}
          disabled={translating}
          onChange={handleLanguage}
        />
      </Form.Item>

      <Form.Item
        name="reason"
        label={<span>Reason text {translating && <Spin size="small" />}</span>}>
        <Input.TextArea
          rows={3}
          placeholder="Optional — why is this being recorded manually?"
          // Retyping a canned sentence makes it no longer that preset, and
          // the dropdown must stop claiming otherwise.
          onChange={event => {
            const matched = presetForText(event.target.value);
            setPreset(
              matched ?? (event.target.value ? CUSTOM_REASON : undefined),
            );
          }}
        />
      </Form.Item>
    </>
  );
};

ManualTxReasonField.propTypes = {
  form: PropTypes.object.isRequired,
  userId: PropTypes.string,
};

export default ManualTxReasonField;
